import { useEffect, useRef, useState } from "react";

/**
 * usePullToRefresh — mobile-friendly pull-to-refresh gesture.
 *
 * Usage:
 *   const { indicatorStyle, refreshing } = usePullToRefresh(onRefresh);
 *   <div style={indicatorStyle}>...</div>
 *
 * Only triggers when the user is at the top of the page (window scrollY = 0)
 * AND pulls down > threshold px. Ignores wheel / trackpad scroll (touch only).
 *
 * @param onRefresh async function called on trigger
 * @param opts { threshold?: number, disabled?: boolean }
 */
export default function usePullToRefresh(onRefresh, opts = {}) {
  const threshold = opts.threshold ?? 70;
  const disabled = opts.disabled ?? false;
  const [pull, setPull] = useState(0); // current drag distance in px
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const armed = useRef(false);

  useEffect(() => {
    if (disabled) return undefined;
    const onTouchStart = (e) => {
      if (window.scrollY > 0) return;
      startY.current = e.touches[0].clientY;
      armed.current = true;
    };
    const onTouchMove = (e) => {
      if (!armed.current || startY.current == null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && window.scrollY === 0) {
        // resistance curve
        setPull(Math.min(dy * 0.5, threshold * 1.5));
      } else {
        setPull(0);
      }
    };
    const onTouchEnd = async () => {
      if (!armed.current) return;
      armed.current = false;
      startY.current = null;
      if (pull >= threshold && !refreshing) {
        setRefreshing(true);
        try {
          await onRefresh?.();
        } finally {
          setRefreshing(false);
          setPull(0);
        }
      } else {
        setPull(0);
      }
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd);
    document.addEventListener("touchcancel", onTouchEnd);
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [pull, refreshing, threshold, disabled, onRefresh]);

  const progress = Math.min(1, pull / threshold);
  return {
    pull,
    refreshing,
    progress,
    // Style for a top-of-page indicator that follows the drag
    indicatorStyle: {
      transform: `translateY(${pull}px)`,
      opacity: pull > 8 ? Math.min(1, progress + 0.2) : 0,
      transition: refreshing ? "transform 0.2s ease" : "none",
    },
  };
}
